import type { App } from '../../app.js';
import { h, button, toast } from '../dom.js';
import { RoomListing } from '@voidline/shared';

export function buildMultiplayerScreen(app: App): HTMLElement {
  const codeInput = h('input', {
    type: 'text',
    placeholder: 'ROOM CODE',
    maxlength: '5',
    style: 'width: 150px; text-transform: uppercase; text-align:center;',
  }) as HTMLInputElement;

  const browser = h('div', { class: 'col', style: 'gap:6px; max-height: 240px; overflow-y:auto;' });
  const browserStatus = h('div', { class: 'subtle', style: 'font-size:12px; text-transform:none;', text: '' });

  const renderRooms = (rooms: RoomListing[]) => {
    browser.innerHTML = '';
    if (rooms.length === 0) {
      browserStatus.textContent = 'No public rooms right now — quickplay will create one.';
      return;
    }
    browserStatus.textContent = `${rooms.length} public room${rooms.length > 1 ? 's' : ''}`;
    for (const r of rooms) {
      browser.appendChild(
        h(
          'button',
          {
            class: 'focusable',
            style: 'text-align:left; padding: 8px 14px; width: 100%;',
            onclick: () => {
              app.audio.play('ui_confirm');
              app.joinRoom(r.code);
            },
          },
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between; width:100%;' },
            h('span', { class: 'mono', text: r.code }),
            h('span', { class: 'subtle', style: 'font-size:11px;', text: `${r.playerCount}/${r.maxPlayers} · ${r.phase.toUpperCase()}` })
          ),
          h('div', { class: 'subtle', style: 'font-size:10px; text-transform:none;', text: `${r.trackId} · ${r.mode}` })
        )
      );
    }
  };

  const refreshRooms = () => {
    browserStatus.textContent = 'Contacting server…';
    app.ensureConnection(() => {
      app.conn!.send({ type: 'listRooms' });
    });
  };

  // hook roomList messages while this screen lives
  const prevHandler = app.onRoomList;
  app.onRoomList = renderRooms;

  const screen = h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel col', style: 'width: min(560px, 94vw); gap: 18px;' },
      h(
        'div',
        { class: 'row', style: 'justify-content:space-between;' },
        h('div', { class: 'h1', text: 'MULTIPLAYER' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.onRoomList = prevHandler;
          app.show('menu');
        }, 'small focusable')
      ),
      h('div', {
        class: 'subtle',
        style: 'text-transform:none;',
        text: `Racing as ${app.settings.playerName || 'PILOT'} — change your callsign in Settings.`,
      }),
      h(
        'div',
        { class: 'row', style: 'gap: 12px;' },
        button('QUICKPLAY', () => {
          app.audio.play('ui_confirm');
          toast('Finding a race…');
          app.quickplay();
        }, 'primary focusable grow'),
        button('CREATE PRIVATE ROOM', () => {
          app.audio.play('ui_confirm');
          app.createRoom(true);
        }, 'focusable grow')
      ),
      h(
        'div',
        { class: 'row', style: 'gap: 12px; align-items:center;' },
        h('span', { class: 'h2', text: 'JOIN WITH CODE' }),
        codeInput,
        button('JOIN', () => {
          const code = codeInput.value.trim().toUpperCase();
          if (code.length < 4) {
            toast('Enter the 5-letter room code.', true);
            return;
          }
          app.audio.play('ui_confirm');
          app.joinRoom(code);
        }, 'small focusable')
      ),
      h(
        'div',
        { class: 'col', style: 'gap: 8px;' },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between;' },
          h('span', { class: 'h2', text: 'PUBLIC ROOMS' }),
          button('REFRESH', refreshRooms, 'small focusable')
        ),
        browserStatus,
        browser
      )
    )
  );

  refreshRooms();
  return screen;
}
